/**
 * Image blob cache — preloaded network fetches are converted into `blob:`
 * URLs and cached here keyed by their original path. Rendered `<img>` tags
 * use `getImageSrc(path)` which returns the blob URL when available and
 * otherwise falls back to the original path (so nothing breaks if a path
 * isn't in the preload/postload lists).
 *
 * Why: the browser will re-hit the network for an `<img src>` even if the
 * same URL was previously fetched via `fetch()` (separate cache keys in
 * some browsers, and HTTP cache is not always honored under Service
 * Workers / CDN configurations). Serving blob URLs guarantees the bytes
 * come from memory, so the UI shows images instantly after preload.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var cache = new Map();
var inFlight = new Map();
export function preloadAsBlob(path) {
    return __awaiter(this, void 0, void 0, function () {
        var existing, pending, p;
        var _this = this;
        return __generator(this, function (_a) {
            existing = cache.get(path);
            if (existing)
                return [2 /*return*/, existing];
            pending = inFlight.get(path);
            if (pending)
                return [2 /*return*/, pending];
            p = (function () { return __awaiter(_this, void 0, void 0, function () {
                var res, blob, url;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, fetch(path)];
                        case 1:
                            res = _a.sent();
                            if (!res.ok)
                                throw new Error("Failed to fetch ".concat(path));
                            return [4 /*yield*/, res.blob()];
                        case 2:
                            blob = _a.sent();
                            url = URL.createObjectURL(blob);
                            cache.set(path, url);
                            inFlight.delete(path);
                            return [2 /*return*/, url];
                    }
                });
            }); })();
            inFlight.set(path, p);
            return [2 /*return*/, p];
        });
    });
}
export function getImageSrc(path) {
    var _a;
    if (!path)
        return path !== null && path !== void 0 ? path : '';
    // Already a blob / data URL — pass through
    if (path.startsWith('blob:') || path.startsWith('data:'))
        return path;
    return (_a = cache.get(path)) !== null && _a !== void 0 ? _a : path;
}
